export const AUDIO_GENERATOR_MODEL = 'bytedance/seed-audio-1.0';
export const AUDIO_GENERATOR_COST = 1000;
export const AUDIO_GENERATOR_MAX_PROMPT = 2048;
export const AUDIO_GENERATOR_DRAFT_KEY = 'audioGeneratorDraft';

/** Only the editable brief and target length cross the signup hand-off. */
export function parseAudioGeneratorDraft(
  value: unknown,
): { prompt: string; duration: number | null } | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const input = value as Record<string, unknown>;
  if (typeof input.prompt !== 'string' || !input.prompt.trim()) return null;
  return {
    prompt: input.prompt.trim().slice(0, AUDIO_GENERATOR_MAX_PROMPT),
    duration:
      typeof input.duration === 'number' &&
      Number.isFinite(input.duration) &&
      input.duration >= 1 &&
      input.duration <= 120
        ? input.duration
        : null,
  };
}
export const AUDIO_REFERENCE_MAX_BYTES = 10 * 1024 * 1024;
export const AUDIO_SAMPLE_RATES = [
  8000, 16000, 24000, 32000, 44100, 48000,
] as const;
export const AUDIO_OUTPUT_FORMATS = ['mp3', 'wav', 'ogg_opus', 'pcm'] as const;
export type AudioOutputFormat = (typeof AUDIO_OUTPUT_FORMATS)[number];

export interface AudioGeneratorRequest {
  prompt: string;
  /** Prompt guidance only; the provider has no exact duration parameter. */
  duration: number | null;
  output_format: AudioOutputFormat;
  sample_rate: number;
  speed: number;
  volume: number;
  pitch: number;
  audios: string[];
  image?: string;
}

export const AUDIO_GENERATOR_DEFAULTS: Omit<AudioGeneratorRequest, 'prompt'> = {
  duration: null,
  output_format: 'mp3',
  sample_rate: 24000,
  speed: 1,
  volume: 1,
  pitch: 0,
  audios: [],
};

export function parseAudioGeneratorRequest(
  body: unknown,
): AudioGeneratorRequest {
  if (!body || typeof body !== 'object' || Array.isArray(body))
    throw new Error('Invalid request body');
  const input = body as Record<string, unknown>;
  if (typeof input.prompt !== 'string' || !input.prompt.trim())
    throw new Error('Describe the audio you want to create.');
  const prompt = input.prompt.trim();
  if (prompt.length > AUDIO_GENERATOR_MAX_PROMPT)
    throw new Error('Keep your description within 2048 characters.');
  const number = (key: string, fallback: number, min: number, max: number) => {
    const value = input[key] === undefined ? fallback : input[key];
    if (
      typeof value !== 'number' ||
      !Number.isFinite(value) ||
      value < min ||
      value > max
    )
      throw new Error(
        `Invalid ${key}. Choose a value between ${min} and ${max}.`,
      );
    return value;
  };
  const duration =
    input.duration == null ? null : number('duration', 30, 1, 120);
  const sample_rate = number('sample_rate', 24000, 8000, 48000);
  if (!AUDIO_SAMPLE_RATES.some((rate) => rate === sample_rate))
    throw new Error('Choose a supported sample rate.');
  const output_format = input.output_format ?? 'mp3';
  if (!AUDIO_OUTPUT_FORMATS.some((format) => format === output_format))
    throw new Error('Choose MP3, WAV, OGG Opus, or PCM.');
  const audios = input.audios ?? [];
  if (
    !Array.isArray(audios) ||
    audios.length > 3 ||
    audios.some((url) => typeof url !== 'string' || url.length > 2048 || !url)
  )
    throw new Error('Attach up to three audio references.');
  if (
    input.image !== undefined &&
    (typeof input.image !== 'string' ||
      !input.image ||
      input.image.length > 2048)
  )
    throw new Error('Invalid image reference.');
  if (audios.length && input.image)
    throw new Error('Use audio references or one image, not both.');
  if (new Set(audios).size !== audios.length)
    throw new Error('Each audio reference must be different.');
  const mentions = [...prompt.matchAll(/@Audio(\d+)/gi)].map((match) =>
    Number(match[1]),
  );
  if (mentions.some((index) => index < 1 || index > audios.length))
    throw new Error('Attach the audio clips referenced in your prompt.');
  return {
    prompt,
    duration,
    output_format: output_format as AudioOutputFormat,
    sample_rate,
    speed: number('speed', 1, 0.5, 2),
    volume: number('volume', 1, 0.5, 2),
    pitch: number('pitch', 0, -12, 12),
    audios,
    ...(input.image ? { image: input.image as string } : {}),
  };
}

export function buildAudioGeneratorPrompt(input: AudioGeneratorRequest) {
  const references = input.audios.length
    ? '\nAudio references are numbered in attachment order: ' +
      input.audios
        .map((_, i) => `@Audio${i + 1} is reference audio ${i + 1}`)
        .join(', ') +
      '.'
    : '';
  return (
    input.prompt +
    references +
    (input.duration
      ? `\nAim for approximately ${input.duration} seconds, with a natural ending.`
      : '')
  );
}

export const AUDIO_PROMPT_STARTERS = [
  {
    kind: 'Narration',
    title: 'A voice that draws you in',
    prompt:
      'A warm, calm narrator says: “Some journeys begin with a map. The best ones begin with curiosity.” Intimate studio recording, natural pauses, no music.',
    duration: 15,
  },
  {
    kind: 'Music',
    title: 'Set the mood',
    prompt:
      'A mellow instrumental with soft electric piano, brushed drums and a warm bass line. A relaxed Sunday morning feeling. No vocals, with a gentle ending.',
    duration: 30,
  },
  {
    kind: 'Dialogue',
    title: 'Bring characters to life',
    prompt:
      'Two friends in a quiet cafe. A bright, curious woman asks, “Same time tomorrow?” A relaxed man smiles and replies, “Wouldn’t miss it.” Cups clink softly in the background.',
    duration: 15,
  },
  {
    kind: 'Soundscape',
    title: 'Build a world in sound',
    prompt:
      'A night train arriving at a quiet platform. Wheels slow with a soft metallic squeal. A calm station announcer says, “Welcome home.” Doors open, footsteps fade into the distance.',
    duration: 20,
  },
] as const;
